<template>
  <div>
    <div class="document-container">
      <div class="columns">
        <div class="column is-three-quarters">
          <div class="document" style="height:100%">
            <div class="header">
              <EsHeader>
                <template #header>
                  <div class="header-title">
                    Process Details
                  </div>
                  <div class="header-subtitle">
                    Displays details about processing.
                  </div>
                </template>
              </EsHeader>
            </div>
            <div class="content">
              <es-form>
                <template #es-form-header>
                  <div class="header-container">
                    <div class="header-elements">
                      <font-awesome-icon :icon="['fas', 'chart-line']" size="lg"/>
                    </div>
                    <div class="header-elements">
                      <h6 class="title">
                        Status Statistics
                      </h6>
                    </div>
                  </div>
                </template>
                <template #es-form>
                  <table v-if="searchObject" class="table is-fullwidth">
                    <thead>
                      <tr>
                        <th class="column-header has-text-grey">
                          Match Status
                        </th>
                        <th class="text-align--right has-text-grey"
                          v-if="!loading && !loadingError && recordGroupCategories.length"
                        >
                          Record Count
                        </th>
                      </tr>
                    </thead>
                    <es-loader v-if="loading" style="margin: 5rem"/>
                    <es-exception
                      v-else-if="loadingError"
                      style="margin: 3.5rem !important"
                      exceptionMessage="There was an issue.
                        Please try again or contact your administrator."
                    />
                    <tbody v-else-if="recordGroupCategories.length">
                      <tr
                        class="search-category has-text-weight-bold"
                        v-for="(category,index) in recordGroupCategories"
                        :key="index"
                      >
                        <td colspan="2" style="border-bottom: 0; padding: 0">
                          <table role="presentation"
                            class="table is-fullwidth"
                            style="margin: 0 1rem 0.5rem 0"
                          >
                            <tbody>
                              <tr class="group-name">
                                <td
                                  class="has-text-weight-bold removeUnderline"
                                  colspan="2"
                                  style="padding-bottom: 0"
                                >
                                  {{ category.group }}
                                </td>
                              </tr>
                              <tr
                                class="row-border"
                                v-for="(subCategory, index) in category.list.filter(l => l.count)"
                                :key="index"
                              >
                                <td
                                  class ="has-text-grey font-weight"
                                  style="padding: 0 0 0 1rem; color: #666 !important"
                                >
                                {{ subCategory.prefix }}: {{ subCategory.description }}
                                </td>
                                <td
                                  class ="text-align--right font-weight record-count"
                                  style="padding: 0 1rem 0 0"
                                >
                                  {{ subCategory.count }}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    </tbody>
                    <tbody v-else>
                      <div style="padding: 3rem">
                        <font-awesome-icon
                        :icon="['fal', 'exclamation-triangle']"
                        size="4x"
                        />
                        <div class="icon-message">
                          No processing status is available.
                        </div>
                      </div>
                    </tbody>
                  </table>
                </template>
              </es-form>
              <es-form>
                <template #es-form-header>
                  <div class="header-container">
                    <div class="header-elements">
                      <font-awesome-icon :icon="['fas', 'clock']" size="lg" />
                    </div>
                    <div class="header-elements">
                      <h6 class="title">
                        Processing Stage
                      </h6>
                    </div>
                  </div>
                </template>
                <template #es-form>
                  <table v-if="searchObject" class="table is-fullwidth">
                    <thead>
                      <th class="column-header has-text-grey">
                        Stage
                      </th>
                      <th class="text-align--right has-text-grey">
                        Processing Date
                      </th>
                    </thead>
                    <tbody v-if="searchObject.processingStages.length">
                        <tr
                          class="search-category"
                          v-for="(item, index) in processingStagesSortAsc"
                          :key="index"
                        >
                          <td class="has-text-grey">
                            {{ item.label }}
                          </td>
                          <td class="text-align--right">
                            {{ item.stageDate | formatDateMMDDYYYYHHMM }}
                          </td>
                        </tr>
                      </tbody>
                  </table>
                </template>
              </es-form>
              <es-form style="padding-top: 0" v-if="searchObject
                  && searchObject.status ==='ERRORED'
                  || searchObject.status ==='ERRORED_VALIDATION'"
                >
                <template #es-form-header>
                    <div class="header-container">
                      <div class="header-elements">
                        <font-awesome-icon
                          :icon="['fas', 'exclamation-triangle']"
                          size="lg"
                        />
                      </div>
                      <div class="header-elements">
                        <h6 class="title">
                          Errors
                        </h6>
                      </div>
                    </div>
                  </template>
                <template #es-form>
                  <table class="table is-fullwidth" v-if="searchObject && errorList.length">
                    <thead>
                      <th class="column-header has-text-grey" colspan="2">
                        Error Information: Validation failed.
                        Fix the errors below and resubmit data.
                      </th>
                    </thead>
                    <tbody>
                      <tr v-for="(error,index) in errorList" :key="index">
                        <td class ="has-text-grey">
                          {{ error }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </template>
              </es-form>
            </div>
          </div>
        </div>
        <div class="column is-one-quarter">
          <div class="document margin-bottom">
            <div class="header">
              <EsHeader>
                <template #header>
                  <div class="header-title">
                    Next Steps
                  </div>
                </template>
              </EsHeader>
            </div>
            <div class="is-flex flex-direction--column flex-align--start" style="padding: 1rem">
              <div class="margin-bottom--small">
                <router-link
                  v-if="canRefreshSearch"
                  :to="{}"
                  @click.native="refreshSearch"
                  class="button is-primary has-no-underline"
                >
                  Refresh
                </router-link>
              </div>
              <div class="margin-bottom">
                <router-link
                  v-if="hasModule('PRSID:DWNLD:DOWNLOAD') && isDownloadTypesAllowed"
                  :to="{ name: 'PidSearchInfoDownload' }"
                  class="button is-primary has-no-underline"
                  aria-label="Download search"
                  role="button"
                >
                  Download
                </router-link>
              </div>
            </div>
          </div>
          <div class="document">
            <div class="header">
              <EsHeader>
                <template #header>
                  <div class="header-title">
                    General Information
                  </div>
                </template>
              </EsHeader>
            </div>
            <div style="padding: 1rem 1rem 0.5rem">
              <table v-if="searchObject" class="form-table table is-fullwidth" role="presentation">
                <tr class="form-row">
                  <td class="text-align--left has-text-grey">
                    {{ getFieldLabel(`PER:${personType}:SEARCH:DISTRICT_CODE`) }}
                  </td>
                  <td class="text-align--right font">
                    <span v-if="districtList.length === 1">
                      {{
                        searchObject.pidSearchDistrictSchoolAssociation[0].district.location.name
                      }}
                      ({{ searchObject.pidSearchDistrictSchoolAssociation[0].districtCode }})
                    </span>
                    <span v-else>
                      Multiple {{ getFieldLabel(`PER:${personType}:SEARCH:DISTRICT_CODE`) }}(s/es)
                    </span>
                  </td>
                </tr>
                <tr class="form-row">
                  <td class="text-align--left has-text-grey">
                    {{ getFieldLabel(`PER:${personType}:SEARCH:SCHOOL_CODE`) }}
                  </td>
                  <td class="text-align--right font">
                    <span v-if="schoolList.length === 1">
                      {{ searchObject.pidSearchDistrictSchoolAssociation[0].school.location.name }}
                      ({{ searchObject.pidSearchDistrictSchoolAssociation[0].schoolCode }})
                    </span>
                    <span v-else>
                      Multiple {{ getFieldLabel(`PER:${personType}:SEARCH:SCHOOL_CODE`) }}(s/es)
                    </span>
                  </td>
                </tr>
                <tr class="form-row">
                  <td class="text-align--left has-text-grey">
                    Extract Date
                  </td>
                  <td class="text-align--right font">
                    {{ formatExtractDate(search.extractDate) }}
                  </td>
                </tr>
                <tr class="form-row">
                  <td class="text-align--left has-text-grey">
                    Extract File
                  </td>
                  <td class="text-align--right max-width font file-name-cell">
                    <span
                      v-if="searchObject.submissionTypeCode === 'FILE'"
                    >
                      {{ searchObject.fileName }}
                    </span>
                    <span v-else>
                      N/A
                    </span>
                  </td>
                </tr>
                <tr class="form-row">
                  <td class="text-align--left has-text-grey">
                    Transmission ID
                  </td>
                  <td class="text-align--right font">
                    <span v-if="search.transmissionId">
                      {{ search.transmissionId }}
                    </span>
                    <span v-else>
                      N/A
                    </span>
                  </td>
                </tr>
                <tr class="form-row">
                  <td class="text-align--left has-text-grey">
                    {{ getFieldLabel(`PER:${personType}:SEARCH:SOURCE_CODE`) }}
                  </td>
                  <td class="text-align--right font">
                    {{ search.sourceCode }}
                  </td>
                </tr>
                <tr class="form-row">
                  <td class="text-align--left has-text-grey">
                    Creation Date
                  </td>
                  <td class="text-align--right font">
                    {{ formatDateTime(searchObject.createdDate) }}
                  </td>
                </tr>
                <tr class="form-row">
                  <td class="text-align--left has-text-grey">
                    Creation User
                  </td>
                  <td class="text-align--right font">
                    {{ search.createdByFirstName +' '+ search.createdByLastName }}
                  </td>
                </tr>
                <tr class="form-row">
                  <td class="text-align--left has-text-grey align-bottom">
                    Delimiter (Record)
                  </td>
                  <td class="text-align--right font">
                    <span v-if="searchObject.fileRecordDelimiter">
                      {{ searchObject.fileRecordDelimiter }}
                    </span>
                    <span v-else>
                      N/A
                    </span>
                  </td>
                </tr>
                <tr class="form-row">
                  <td class="text-align--left has-text-grey">
                    Delimiter (Header)
                  </td>
                  <td class="text-align--right font">
                    <span v-if="searchObject.fileRecordQualifier">
                      {{ searchObject.fileRecordQualifier }}
                    </span>
                    <span v-else>
                      N/A
                    </span>
                  </td>
                </tr>
                <tr class="form-row">
                  <td class="text-align--left has-text-grey" style="border-style: none">
                    File Version
                  </td>
                  <td class="text-align--right font" style="border-style: none">
                    <span v-if="searchObject.fileVersion">
                      {{ searchObject.fileVersion }}
                    </span>
                    <span v-else>
                      N/A
                    </span>
                  </td>
                </tr>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faChartLine,
  faClock,
  faExclamationTriangle,
} from '@fortawesome/pro-solid-svg-icons';
import EsException from '@/components/EsException.vue';
import EsForm from '@/components/EsForm.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import FieldLabels from '@/mixins/FieldLabels';
import PermissionManager from '@/mixins/PermissionManager';

library.add(
  faChartLine,
  faClock,
  faExclamationTriangle,
);
export default {
  mixins: [
    FieldLabels,
    PermissionManager,
  ],
  components: {
    EsException,
    EsForm,
    EsHeader,
    EsLoader,
  },
  props: [
    'search',
  ],
  data() {
    return this.initialData();
  },
  async created() {
    this.userPersonTypesCodes = this.userPersonTypes
      .map(item => item.code);
  },
  computed: {
    ...mapGetters([
      'userPersonTypes',
    ]),
    ...mapGetters('lookups', [
      'pidTransactionStatuses',
      'defaultPersonType',
    ]),
    canRefreshSearch() {
      if (this.searchObject.status === 'LOADED'
      || this.searchObject.status === 'QUEUED'
      || this.searchObject.status === 'VALIDATION_COMPLETE'
      || this.searchObject.status === 'RUNNING_MATCHING'
      || this.searchObject.status === 'VALIDATION_IN_PROGRESS') {
        return true;
      }
      return false;
    },
    districtList() {
      if (!this.searchObject.pidSearchDistrictSchoolAssociation) return [];
      const temp = this.searchObject.pidSearchDistrictSchoolAssociation.map(a => a.districtCode);
      return [...new Set(temp)];
    },
    errorList() {
      if (this.searchObject && this.searchObject.processingMsgs) {
        const splitedErrorList = this.searchObject.processingMsgs.split('INFO:').filter(val => val !== '');
        return splitedErrorList;
      }
      return null;
    },
    recordGroupCategories() {
      return this.allUsersAndRecordGroupCategories();
    },
    schoolList() {
      if (!this.searchObject.pidSearchDistrictSchoolAssociation) return [];
      const temp = this.searchObject.pidSearchDistrictSchoolAssociation.map(a => a.schoolCode);
      return [...new Set(temp)];
    },
    processingStagesSortAsc() {
      return this.searchObject.processingStages.slice().sort((a, b) => a.stageDate - b.stageDate);
    },
    allowedExtractDownloadTypes() {
      return this.$store.state.propertiesModule.properties['PERSON-EXTRACT_DOWNLOAD_TYPES_ALLOWED'];
    },
    isDownloadTypesAllowed() {
      if (this.allowedExtractDownloadTypes) {
        return this.allowedExtractDownloadTypes.includes(this.searchObject.submissionTypeCode);
      }
      return null;
    },
    personType() {
      if (this.userPersonTypesCodes.includes(this.defaultPersonType)) {
        return this.defaultPersonType;
      } return this.userPersonTypesCodes[0];
    },
  },
  mounted() {
    this.allUsersAndRecordGroupCategories();
  },
  methods: {
    ...mapActions('lookups', [
      'loadPidTransactionStatuses',
    ]),
    allUsersAndRecordGroupCategories() {
      const tranStatusGroups = JSON.parse(JSON.stringify(this.pidTransactionStatuses));
      this.loading = true;
      this.loadingError = false;
      try {
        this.searchObject.pidSearchTransStatusCountAssociation.forEach((tranStatus) => {
          tranStatusGroups.forEach((group, outerIndex) => {
            if (group.list.map(n => n.id).flat().includes(tranStatus.status)) {
              tranStatusGroups[outerIndex].list.forEach((s, innerIndex) => {
                if (s.id === tranStatus.status) {
                  tranStatusGroups[outerIndex].list[innerIndex].count = tranStatus.statusCount;
                  tranStatusGroups[outerIndex].showThisGroup = true;
                }
              });
            }
          });
        });
      } catch (error) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
      return tranStatusGroups.filter(x => x.showThisGroup);
    },
    formatDateTime(value) {
      return moment(value).format('MM/DD/YYYY h:mmA');
    },
    formatExtractDate(value) {
      return moment(value).format('MM/DD/YYYY');
    },
    initialData() {
      return {
        searchObject: this.search,
        loading: false,
        loadingError: false,
        userPersonTypesCodes: null,
      };
    },
    refreshSearch() {
      this.$emit('refreshTheSearch');
    },
  },
};
</script>

<style lang="sass" scoped>
  td.align-bottom
    vertical-align: bottom
  .search-category:last-child
    border-bottom: none
  .search-category:last-child table .row-border:last-child
    border-bottom: none
  .column-header
    padding-left: 0px
  .font
    font-size: 14px
  .form-row
    line-height: 2rem
    padding-right: 0.5rem
  .font-weight
    font-weight: normal
  .has-text-light-grey
    color: #999999
  .icon-message
    font-size: 16px
    padding: 2rem 0 0 0
  .loader-center
    text-align: center
    vertical-align: middle
    font-size: 24px
  .margin-bottom
    margin-bottom: 1rem
  .margin-bottom--small
    margin-bottom: 0.5rem
  .max-width
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis
    max-width: 20ch
  .record-count
    font-size: 14px
    color: #000 !important
  .removeUnderline
    border-bottom: 1px solid transparent !important
  .row-border
    border-bottom: 1px solid #dee2e6
  .text-align--left
    text-align: left !important
    padding-left: 0
  .text-align--right
    text-align: right !important
    padding-right: 0
  .table
    table-layout: fixed
    .file-name-cell
      overflow-wrap: break-word
      white-space: normal
  @media screen and (max-width: 70em)
    .columns
      display: block
      .column
        width: 100%
</style>
